import { z } from 'zod';
import { MILLISECONDS_PER_DAY } from './dates.js';
export const RecurrenceFrequencySchema = z.enum([
    'DAILY',
    'WEEKLY',
    'FORTNIGHTLY',
    'MONTHLY',
    'QUARTERLY',
    'ANNUALLY',
]);
/** Validated recurrence rule used to compute next run dates. */
export const RecurrenceRuleSchema = z.object({
    frequency: RecurrenceFrequencySchema,
    /** Number of frequency units between occurrences (e.g. 2 = "every 2 weeks"). */
    interval: z.number().int().min(1).default(1),
    /**
     * Weekdays to run on, 0 = Sunday … 6 = Saturday, matching JS `Date#getDay()`. Only
     * meaningful for WEEKLY.
     */
    daysOfWeek: z.array(z.number().int().min(0).max(6)).default([]),
    /** Day of month to run on (1–31). Clamped to last day if month is shorter. */
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    /** Month of year (1–12). Only used for ANNUALLY frequency. */
    monthOfYear: z.number().int().min(1).max(12).optional(),
});
/** Maps a persisted recurring invoice/expense record to a {@link RecurrenceRule}. */
export function toRecurrenceRule(record) {
    return {
        frequency: record.frequency,
        interval: record.interval,
        daysOfWeek: record.daysOfWeek,
        dayOfMonth: record.dayOfMonth ?? undefined,
        monthOfYear: record.monthOfYear ?? undefined,
    };
}
/** FORTNIGHTLY/QUARTERLY predate the `interval` field — their real cadence is fixed. */
export const LEGACY_FREQUENCY_INTERVAL = {
    FORTNIGHTLY: 2,
    QUARTERLY: 3,
};
/**
 * Discriminant for {@link EndConditionSchema}, shared with UI components that toggle on
 * it.
 */
export const EndConditionTypeSchema = z.enum(['never', 'after_n', 'on_date']);
/** Discriminated union describing when a recurring invoice schedule ends. */
export const EndConditionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal(EndConditionTypeSchema.enum.never) }),
    z.object({
        type: z.literal(EndConditionTypeSchema.enum.after_n),
        maxRuns: z.number().int().min(1),
    }),
    z.object({
        type: z.literal(EndConditionTypeSchema.enum.on_date),
        endDate: z.string().date(),
    }),
]);
const SV_FMT_DATE = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
};
const SV_FMT_DATETIME = {
    ...SV_FMT_DATE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
};
/* Positive offset = east of UTC (ahead); negative = west of UTC (behind). */
function getUtcOffsetMs(date, tz) {
    // sv-SE locale reliably produces "YYYY-MM-DD HH:mm:ss" in the target timezone
    const formatter = new Intl.DateTimeFormat('sv-SE', { timeZone: tz, ...SV_FMT_DATETIME });
    const localStr = formatter.format(date);
    const [datePart, timePart] = localStr.split(' ');
    const localAsUtcMs = new Date(`${datePart}T${timePart}Z`).getTime();
    return localAsUtcMs - date.getTime();
}
function getLocalDateParts(date, tz) {
    const formatter = new Intl.DateTimeFormat('sv-SE', { timeZone: tz, ...SV_FMT_DATE });
    const [year, month, day] = formatter.format(date).split('-').map(Number);
    return { year, month, day };
}
/*
 * UTC offset is sampled at UTC midnight on the target date — within one hour of local
 * midnight and correct for all standard scheduling purposes, including across DST boundaries.
 */
function localMidnightUtc(year, month, day, tz) {
    const utcMidnightMs = Date.UTC(year, month - 1, day);
    const offsetMs = getUtcOffsetMs(new Date(utcMidnightMs), tz);
    return new Date(utcMidnightMs - offsetMs);
}
function clampDay(year, month, day) {
    // new Date(year, month, 0) = day 0 of (month) = last day of (month - 1)
    // With month 1-indexed: new Date(year, month, 0).getDate() = last day of month
    return Math.min(day, new Date(year, month, 0).getDate());
}
// Pure calendar-date arithmetic — the Date is only re-decomposed into y/m/d, never read as an instant, so host timezone doesn't matter.
function addCalendarDays(year, month, day, deltaDays) {
    const date = new Date(year, month - 1, day + deltaDays);
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}
// Weekday is a pure calendar fact independent of timezone — safe to derive from y/m/d via the host's local Date constructor.
function getDayOfWeek(year, month, day) {
    return new Date(year, month - 1, day).getDay();
}
/**
 * Computes the next scheduled run date after `after`, advancing by the rule's frequency
 * in the organisation's timezone. Always returns midnight local time.
 */
export function computeNextRunAt(rule, after, tz) {
    const { year, month, day } = getLocalDateParts(after, tz);
    switch (rule.frequency) {
        case 'DAILY': {
            const nextUtc = new Date(after.getTime() + rule.interval * MILLISECONDS_PER_DAY);
            const { year: ny, month: nm, day: nd } = getLocalDateParts(nextUtc, tz);
            return localMidnightUtc(ny, nm, nd, tz);
        }
        case 'WEEKLY': {
            if (rule.daysOfWeek.length === 0) {
                const nextUtc = new Date(after.getTime() + rule.interval * 7 * MILLISECONDS_PER_DAY);
                const { year: ny, month: nm, day: nd } = getLocalDateParts(nextUtc, tz);
                return localMidnightUtc(ny, nm, nd, tz);
            }
            const weekday = getDayOfWeek(year, month, day);
            const sortedDays = [...rule.daysOfWeek].sort((a, b) => a - b);
            const laterThisWeek = sortedDays.find((candidateDay) => candidateDay > weekday);
            if (laterThisWeek !== undefined) {
                const { year: ny, month: nm, day: nd, } = addCalendarDays(year, month, day, laterThisWeek - weekday);
                return localMidnightUtc(ny, nm, nd, tz);
            }
            const startOfWeek = addCalendarDays(year, month, day, -weekday);
            const startOfTargetWeek = addCalendarDays(startOfWeek.year, startOfWeek.month, startOfWeek.day, rule.interval * 7);
            const { year: ny, month: nm, day: nd, } = addCalendarDays(startOfTargetWeek.year, startOfTargetWeek.month, startOfTargetWeek.day, sortedDays[0]);
            return localMidnightUtc(ny, nm, nd, tz);
        }
        case 'FORTNIGHTLY':
            return computeNextRunAt({
                ...rule,
                frequency: 'WEEKLY',
                interval: LEGACY_FREQUENCY_INTERVAL.FORTNIGHTLY,
                daysOfWeek: [],
            }, after, tz);
        case 'MONTHLY': {
            const totalMonths = month - 1 + rule.interval;
            const nextYear = year + Math.floor(totalMonths / 12);
            const nextMonth = (totalMonths % 12) + 1;
            const targetDay = clampDay(nextYear, nextMonth, rule.dayOfMonth ?? day);
            return localMidnightUtc(nextYear, nextMonth, targetDay, tz);
        }
        case 'QUARTERLY':
            return computeNextRunAt({ ...rule, frequency: 'MONTHLY', interval: LEGACY_FREQUENCY_INTERVAL.QUARTERLY }, after, tz);
        case 'ANNUALLY': {
            const nextYear = year + rule.interval;
            const targetMonth = rule.monthOfYear ?? month;
            const targetDay = clampDay(nextYear, targetMonth, rule.dayOfMonth ?? day);
            return localMidnightUtc(nextYear, targetMonth, targetDay, tz);
        }
    }
}
const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
/** Weekday abbreviations, 0 = Sunday … 6 = Saturday. */
export const DAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function getOrdinal(number) {
    let ord = 'th';
    if (number % 10 == 1 && number % 100 != 11) {
        ord = 'st';
    }
    else if (number % 10 == 2 && number % 100 != 12) {
        ord = 'nd';
    }
    else if (number % 10 == 3 && number % 100 != 13) {
        ord = 'rd';
    }
    return `${number}${ord}`;
}
/** Maps `Date#getDay()` index (Sun=0) to a Monday-first sort key. */
function mondayFirstSortKey(dayIndex) {
    return (dayIndex + 6) % 7;
}
function formatDaysOfWeek(daysOfWeek) {
    return [...daysOfWeek]
        .sort((a, b) => mondayFirstSortKey(a) - mondayFirstSortKey(b))
        .map((dayIndex) => DAY_ABBREVIATIONS[dayIndex])
        .join(', ');
}
/** Returns a human-readable description of the recurrence rule. */
export function formatRecurrence(rule) {
    switch (rule.frequency) {
        case 'DAILY':
            return rule.interval === 1 ? 'Every day' : `Every ${rule.interval} days`;
        case 'WEEKLY': {
            const cadence = rule.interval === 1 ? 'Every week' : `Every ${rule.interval} weeks`;
            return rule.daysOfWeek.length === 0
                ? cadence
                : `${cadence} on ${formatDaysOfWeek(rule.daysOfWeek)}`;
        }
        case 'FORTNIGHTLY':
            return 'Every 2 weeks';
        case 'MONTHLY': {
            const cadence = rule.interval === 1 ? 'Monthly' : `Every ${rule.interval} months`;
            const day = rule.dayOfMonth != null ? ` on the ${getOrdinal(rule.dayOfMonth)}` : '';
            return `${cadence}${day}`;
        }
        case 'QUARTERLY': {
            const day = rule.dayOfMonth != null ? ` on the ${getOrdinal(rule.dayOfMonth)}` : '';
            return `Quarterly${day}`;
        }
        case 'ANNUALLY': {
            const cadence = rule.interval === 1 ? 'Annually' : `Every ${rule.interval} years`;
            const month = rule.monthOfYear != null ? ` in ${MONTH_NAMES[rule.monthOfYear - 1]}` : '';
            const day = rule.dayOfMonth != null ? ` on the ${getOrdinal(rule.dayOfMonth)}` : '';
            return `${cadence}${month}${day}`;
        }
    }
}
//# sourceMappingURL=recurrence.js.map